import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  HostListener,
  inject,
  input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import {
  DateRangeSelection,
  FiscalMonth
} from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';
import { CompareService } from '@portal/app/shared/services/compare.service';
import { DateTimeService } from '@portal/app/shared/services/date-time.service';
import {
  FiscalDatePresetService,
  PresetOption
} from '@portal/app/shared/services/fiscal-date-preset.service';
import { DateRange } from '@portal/app/shared/types';
import {
  clampCompareRange,
  isCompareUnavailable
} from '@portal/app/shared/utils/compare-range.util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FISCAL_DEFAULT_COMPARE_PRESET } from '@portal/app/shared/constants/fiscal.const';

dayjs.extend(utc);

type InputDateType =
  | 'startDate'
  | 'endDate'
  | 'startDateCompare'
  | 'endDateCompare';

type SelectionSource = 'calendar' | 'input';

@Component({
  selector: 'portal-fiscal-calendar-popup',
  templateUrl: './fiscal-calendar-popup.component.html',
  styleUrls: ['./fiscal-calendar-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FiscalCalendarPopupComponent implements OnInit {
  readonly startDate = input<string>('');
  readonly endDate = input<string>('');
  readonly previousStartDate = input<string | null>(null);
  readonly previousEndDate = input<string | null>(null);
  readonly months = input<FiscalMonth[]>([]);
  readonly minDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly maxDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly relativeDate = input<string | null>(null);
  readonly initialCompareOn = input<boolean | undefined>(undefined);

  @Output() readonly dateChanged = new EventEmitter<{
    startDate: string;
    endDate: string;
    previousStartDate: string | null;
    previousEndDate: string | null;
    presetValue: string;
  }>();

  @Output() readonly cancelled = new EventEmitter<void>();
  @Output() readonly compareToggled = new EventEmitter<boolean>();

  compareToggledOn = false;
  compareEnabled = true;
  compareUnavailable = false;
  compareRangeClamped = false;
  compareDropdownKey = 0;

  availabilityInfoText = '';
  isApplyButtonDisabled = false;

  datePresetOptions: readonly PresetOption[] = [];
  selectedDatePreset = 'custom';
  appliedDatePreset = 'custom';

  comparePresetOptions: readonly PresetOption[] = [];
  selectedComparePreset = FISCAL_DEFAULT_COMPARE_PRESET;

  readonly selected = signal<DateRange>(this.createDefaultDateRange());
  readonly selectedCompare = signal<DateRange>(this.createDefaultDateRange());

  readonly displayDates = computed(() => {
    const selectedRange = this.selected();
    const compareRange = this.selectedCompare();
    const formatDate = (date: dayjs.Dayjs) => date.format(this.displayFormat);
    return {
      start: formatDate(selectedRange.startDate),
      end: formatDate(selectedRange.endDate),
      prevStart: formatDate(compareRange.startDate),
      prevEnd: formatDate(compareRange.endDate)
    };
  });

  readonly calendarDates = computed(() => {
    const selected = this.selected();
    const selectedCompare = this.selectedCompare();
    return {
      start: this.formatCalendarDate(selected.startDate),
      end: this.formatCalendarDate(selected.endDate),
      compareStart: this.formatCalendarDate(selectedCompare.startDate),
      compareEnd: this.formatCalendarDate(selectedCompare.endDate)
    };
  });

  readonly formattedMinDate = computed(() =>
    this.formatDateBound(this.minDate())
  );

  readonly formattedMaxDate = computed(() =>
    this.formatDateBound(this.maxDate())
  );

  private cachedSelected: DateRange = this.createDefaultDateRange();
  private cachedSelectedCompare: DateRange = this.createDefaultDateRange();
  private cachedCompareToggledOn = false;
  private cachedDatePreset = 'custom';
  private cachedComparePreset = FISCAL_DEFAULT_COMPARE_PRESET;
  private cachedCompareRangeClamped = false;

  private isApplying = false;
  private lastSelectionSource: SelectionSource = 'input';
  private readonly displayFormat = 'MMM D, YYYY';
  private readonly editFormat = 'MM-DD-YYYY';
  private readonly calendarDateFormat = 'YYYY-MM-DD';

  private readonly dateService = inject(DateTimeService);
  private readonly compareService = inject(CompareService);
  private readonly fiscalDatePresetService = inject(FiscalDatePresetService);

  ngOnInit(): void {
    const initialCompare = this.initialCompareOn();
    if (initialCompare !== undefined) {
      this.compareToggledOn = initialCompare;
    } else {
      this.compareToggledOn = this.compareService.compare;
    }
    this.compareEnabled = this.compareService.compareIsAvailable;
    if (!this.compareEnabled) {
      this.compareToggledOn = false;
    }
    this.availabilityInfoText = `Latest complete data available from ${this.formatDate(
      this.dateService.maximumEndDate(),
      'MMM D, YYYY'
    )}`;

    this.datePresetOptions = this.sortByOrder(
      this.fiscalDatePresetService.datePresetOptions
    );
    this.comparePresetOptions = this.sortByOrder(
      this.fiscalDatePresetService.comparePresetOptions
    );

    const savedPreset = this.relativeDate();
    if (savedPreset && savedPreset !== 'custom_range') {
      const matchingOption = this.datePresetOptions.find(
        (o) => o.value === savedPreset
      );
      if (matchingOption) {
        this.selectedDatePreset = savedPreset;
        this.appliedDatePreset = savedPreset;
      }
    }

    this.initializeDatesFromInputs();
    this.deriveComparePreset();
    this.resetCompareRangeIfOutOfBounds();
    this.updateCompareAvailability();
    this.syncClampBannerIfCompareAtMin();
    this.cacheCurrentValues();
  }

  private initializeDatesFromInputs(): void {
    if (this.startDate() && this.endDate()) {
      this.selected.set(this.createDateRange(this.startDate(), this.endDate()));
    }
    const prevStart = this.previousStartDate();
    const prevEnd = this.previousEndDate();
    if (prevStart && prevEnd) {
      this.selectedCompare.set(this.createDateRange(prevStart, prevEnd));
    }
  }

  // Reopen: saved compare already sits on minDate, so clampCompareRange won't set the flag.
  private syncClampBannerIfCompareAtMin(): void {
    const min = this.minDate();
    if (
      !this.compareToggledOn ||
      !this.compareEnabled ||
      !min ||
      !this.selectedCompare().startDate.isSame(min, 'day')
    ) {
      return;
    }
    this.compareRangeClamped = true;
  }

  onFiscalDateSelected(event: DateRangeSelection): void {
    if (!event.endDate) return;
    this.lastSelectionSource = 'calendar';

    const currentSelected = this.selected();
    const primaryChanged =
      !currentSelected.startDate.isSame(
        dayjs(event.startDate).utc(true),
        'day'
      ) ||
      !currentSelected.endDate.isSame(dayjs(event.endDate).utc(true), 'day');

    if (primaryChanged) {
      this.selectedDatePreset = 'custom';
    } else {
      this.selectedComparePreset = 'custom';
    }

    this.handleCalendarSelection(event);

    if (primaryChanged && this.compareToggledOn && this.compareEnabled) {
      this.selectedComparePreset = FISCAL_DEFAULT_COMPARE_PRESET;
      this.recalculateCompareRange();
    } else if (primaryChanged) {
      this.updateCompareAvailability();
    } else {
      this.clearClampInfoIfStartLeftMin(this.selectedCompare().startDate);
    }
  }

  onInputFocus(event: FocusEvent, type: InputDateType): void {
    const inputEl = event.target as HTMLInputElement;
    inputEl.value = this.getDateForType(type).format(this.editFormat);
  }

  onInputBlur(event: FocusEvent, type: InputDateType): void {
    const inputEl = event.target as HTMLInputElement;
    inputEl.value = this.getDateForType(type).format(this.displayFormat);
  }

  private readonly dateTypeMap: Record<InputDateType, () => dayjs.Dayjs> = {
    startDate: () => this.selected().startDate,
    endDate: () => this.selected().endDate,
    startDateCompare: () => this.selectedCompare().startDate,
    endDateCompare: () => this.selectedCompare().endDate
  };

  private getDateForType(type: InputDateType): dayjs.Dayjs {
    return this.dateTypeMap[type]();
  }

  onInputChange(event: Event, type: InputDateType): void {
    this.lastSelectionSource = 'input';
    const parsed = this.parseInputDate(
      (event.target as HTMLInputElement).value
    );
    if (!parsed.isValid()) return;

    // Default dates before the minimum to the minimum.
    const date = this.clampToMinDate(parsed);
    if (!this.isWithinBounds(date)) return;

    // The comparison period must stay before the current period start
    // (mirrors the standard calendar's compare date validation).
    if (
      (type === 'startDateCompare' || type === 'endDateCompare') &&
      !date.isBefore(this.selected().startDate, 'day')
    ) {
      return;
    }

    if (type === 'startDate' || type === 'endDate') {
      this.selectedDatePreset = 'custom';
    } else {
      this.selectedComparePreset = 'custom';
    }

    this.handleInputDate(date, type);

    if (
      (type === 'startDate' || type === 'endDate') &&
      this.compareToggledOn &&
      this.compareEnabled
    ) {
      this.selectedComparePreset = FISCAL_DEFAULT_COMPARE_PRESET;
      this.recalculateCompareRange();
    } else if (type === 'startDate' || type === 'endDate') {
      this.updateCompareAvailability();
    } else if (type === 'startDateCompare') {
      this.clearClampInfoIfStartLeftMin(this.selectedCompare().startDate);
    }
  }

  onDatePresetChange(value: string): void {
    this.selectedDatePreset = value;
    this.lastSelectionSource = 'input';
    const range = this.fiscalDatePresetService.calculateDateRange(
      value,
      this.maxDate()
    );
    if (range) {
      this.selected.set({ startDate: range.startDate, endDate: range.endDate });
      if (this.compareToggledOn && this.compareEnabled) {
        this.selectedComparePreset = FISCAL_DEFAULT_COMPARE_PRESET;
        this.recalculateCompareRange();
      } else {
        this.updateCompareAvailability();
      }
    }
  }

  onComparePresetChange(value: string): void {
    this.selectedComparePreset = value;
    this.lastSelectionSource = 'input';
    this.recalculateCompareRange();
  }

  onCompareToggle(checked: boolean): void {
    this.compareToggledOn = checked;
    this.compareToggled.emit(checked);
    if (checked) {
      this.selectedComparePreset = FISCAL_DEFAULT_COMPARE_PRESET;
      this.recalculateCompareRange();
    } else {
      this.compareRangeClamped = false;
    }
  }

  apply(): void {
    this.isApplyButtonDisabled = true;
    this.isApplying = true;
    if (this.lastSelectionSource === 'input') {
      this.syncDatesFromInputFields();
    }
    this.resetCompareRangeIfOutOfBounds();
    this.appliedDatePreset = this.selectedDatePreset;
    this.emitDateRangeData();
  }

  @HostListener('document:keydown.escape')
  onEscKey(): void {
    this.cancel();
  }

  cancel(): void {
    if (this.isApplying) {
      this.isApplying = false;
      return;
    }
    this.restoreCachedValues();
    this.updateCompareAvailability();
    this.cancelled.emit();
  }

  private deriveComparePreset(): void {
    if (!this.compareToggledOn || !this.compareEnabled) return;
    const currentRange = this.selected();
    const savedCompare = this.selectedCompare();

    for (const option of this.comparePresetOptions) {
      if (option.value === 'custom') continue;
      const calculated = this.fiscalDatePresetService.calculateCompareDateRange(
        option.value,
        { startDate: currentRange.startDate, endDate: currentRange.endDate },
        this.selectedDatePreset
      );
      if (
        calculated &&
        calculated.startDate.isSame(savedCompare.startDate, 'day') &&
        calculated.endDate.isSame(savedCompare.endDate, 'day')
      ) {
        this.selectedComparePreset = option.value;
        return;
      }
    }
    this.selectedComparePreset = 'custom';
  }

  private resetCompareRangeIfOutOfBounds(): void {
    if (!this.compareToggledOn || !this.compareEnabled) return;
    if (
      this.selectedCompare().endDate.isBefore(this.selected().startDate, 'day')
    ) {
      return;
    }
    this.selectedComparePreset = FISCAL_DEFAULT_COMPARE_PRESET;
    this.recalculateCompareRange();
  }

  private recalculateCompareRange(): void {
    const currentRange = this.selected();
    const compareRange = this.fiscalDatePresetService.calculateCompareDateRange(
      this.selectedComparePreset,
      { startDate: currentRange.startDate, endDate: currentRange.endDate },
      this.selectedDatePreset
    );
    if (!compareRange) {
      this.updateCompareAvailability();
      return;
    }

    const next = clampCompareRange(
      compareRange,
      this.minDate(),
      this.selected().startDate
    );
    this.selectedCompare.set({
      startDate: next.range.startDate,
      endDate: next.range.endDate
    });
    this.compareRangeClamped = next.clampedToMin;
    if (next.clamped) {
      this.selectedComparePreset = 'custom';
      this.compareDropdownKey++;
    }
    this.updateCompareAvailability();
  }

  // Force compare off when current start is already at minDate.
  private updateCompareAvailability(): void {
    this.compareUnavailable = isCompareUnavailable(
      this.selected().startDate,
      this.minDate()
    );
    if (this.compareUnavailable && this.compareToggledOn) {
      this.compareToggledOn = false;
      this.compareToggled.emit(false);
      this.compareRangeClamped = false;
    }
  }

  private clearClampInfoIfStartLeftMin(start: dayjs.Dayjs): void {
    const min = this.minDate();
    if (this.compareRangeClamped && min && !start.isSame(min, 'day')) {
      this.compareRangeClamped = false;
    }
  }

  private handleCalendarSelection(event: DateRangeSelection): void {
    this.selected.set(this.createDateRange(event.startDate, event.endDate!));
    if (event.previousStartDate && event.previousEndDate) {
      this.selectedCompare.set(
        this.createDateRange(event.previousStartDate, event.previousEndDate)
      );
    }
  }

  private handleInputDate(date: dayjs.Dayjs, type: InputDateType): void {
    switch (type) {
      case 'startDate':
        this.selected.update((c) => ({
          startDate: date,
          endDate: date.isAfter(c.endDate) ? date : c.endDate
        }));
        break;
      case 'endDate':
        this.selected.update((c) => ({
          startDate: c.startDate,
          endDate: date.isBefore(c.startDate) ? c.startDate : date
        }));
        break;
      case 'startDateCompare':
        this.selectedCompare.update((c) =>
          date.isAfter(c.endDate) ? c : { startDate: date, endDate: c.endDate }
        );
        break;
      case 'endDateCompare':
        this.selectedCompare.update((c) =>
          date.isBefore(c.startDate)
            ? c
            : { startDate: c.startDate, endDate: date }
        );
        break;
    }
  }

  private syncDatesFromInputFields(): void {
    const dates = this.displayDates();
    const currentStart = this.parseInputDate(dates.start);
    const currentEnd = this.parseInputDate(dates.end);
    if (currentStart.isValid() && currentEnd.isValid()) {
      this.selected.set(this.createDateRange(currentStart, currentEnd));
    }
    if (this.compareToggledOn) {
      const compareStart = this.parseInputDate(dates.prevStart);
      const compareEnd = this.parseInputDate(dates.prevEnd);
      if (compareStart.isValid() && compareEnd.isValid()) {
        this.selectedCompare.set(
          this.createDateRange(compareStart, compareEnd)
        );
      }
    }
  }

  private cacheCurrentValues(): void {
    this.cachedSelected = { ...this.selected() };
    this.cachedSelectedCompare = { ...this.selectedCompare() };
    this.cachedCompareToggledOn = this.compareToggledOn;
    this.cachedDatePreset = this.selectedDatePreset;
    this.cachedComparePreset = this.selectedComparePreset;
    this.cachedCompareRangeClamped = this.compareRangeClamped;
  }

  private restoreCachedValues(): void {
    this.selected.set({ ...this.cachedSelected });
    this.selectedCompare.set({ ...this.cachedSelectedCompare });
    this.compareToggledOn = this.cachedCompareToggledOn;
    this.selectedDatePreset = this.cachedDatePreset;
    this.selectedComparePreset = this.cachedComparePreset;
    this.compareRangeClamped = this.cachedCompareRangeClamped;
  }

  private emitDateRangeData(): void {
    const dates = this.calendarDates();
    this.compareService.currentDate = this.selected();
    this.compareService.previousDate = this.selectedCompare();
    this.compareService.compare = this.compareToggledOn;
    this.compareService.setCompareValue(this.compareToggledOn);
    this.cacheCurrentValues();

    this.dateChanged.emit({
      startDate: dates.start,
      endDate: dates.end,
      previousStartDate: this.compareToggledOn ? dates.compareStart : null,
      previousEndDate: this.compareToggledOn ? dates.compareEnd : null,
      presetValue: this.appliedDatePreset
    });
  }

  private formatDateBound(date: dayjs.Dayjs | undefined): string | null {
    return date ? date.format(this.calendarDateFormat) : null;
  }

  private clampToMinDate(date: dayjs.Dayjs): dayjs.Dayjs {
    const min = this.minDate();
    return min && date.isBefore(min, 'day') ? min : date;
  }

  private isWithinBounds(date: dayjs.Dayjs): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min && date.isBefore(min, 'day')) return false;
    if (max && date.isAfter(max, 'day')) return false;
    return true;
  }

  private createDefaultDateRange(): DateRange {
    const now = dayjs().utc(true);
    return { startDate: now, endDate: now };
  }

  private createDateRange(
    start: string | Date | dayjs.Dayjs,
    end: string | Date | dayjs.Dayjs
  ): DateRange {
    return {
      startDate: this.toUtcDayjs(start),
      endDate: this.toUtcDayjs(end)
    };
  }

  private toUtcDayjs(value: string | Date | dayjs.Dayjs): dayjs.Dayjs {
    return dayjs.isDayjs(value) ? value.utc(true) : dayjs(value).utc(true);
  }

  private parseInputDate(value: string): dayjs.Dayjs {
    const editParsed = dayjs(value, this.editFormat, true).utc(true);
    if (editParsed.isValid()) return editParsed;
    return dayjs(value, this.displayFormat).utc(true);
  }

  private formatCalendarDate(date: dayjs.Dayjs): string {
    return date.format(this.calendarDateFormat);
  }

  formatDate(date: dayjs.Dayjs, template = this.displayFormat): string {
    return date.format(template);
  }

  private sortByOrder(options: readonly PresetOption[]): PresetOption[] {
    return [...options].sort((a, b) => a.order - b.order);
  }
}
